import { useQuery } from "@tanstack/react-query";
import {
  LuActivity,
  LuDatabase,
  LuArchive,
  LuHardDrive,
  LuCircleCheck,
  LuCircleX,
  LuLoader,
  LuClock,
  LuTimer,
  LuTriangleAlert,
} from "react-icons/lu";
import type { ComponentType } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";

type Health = Awaited<ReturnType<typeof api.adminHealth>>;

const SERVICES: Array<{
  key: keyof Health["checks"];
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
}> = [
  {
    key: "database",
    label: "Database (D1)",
    description: "Primary product datastore. Must answer a SELECT 1.",
    icon: LuDatabase,
  },
  {
    key: "kv",
    label: "Sessions (KV)",
    description: "Workers KV namespace used for session lookups.",
    icon: LuArchive,
  },
  {
    key: "r2",
    label: "Attachments (R2)",
    description: "Object store backing email attachments.",
    icon: LuHardDrive,
  },
];

export function StatusPage() {
  // Intervals tuned to keep public-worker request count under control.
  // Health hits D1 + KV + R2 every poll, so the previous 15s loop alone
  // burned ~5.7k requests/day before we accounted for any other tab.
  // 60s health is still well within "operator notices a problem within
  // a minute" SLO.
  const health = useQuery({
    queryKey: ["console", "admin", "health"],
    queryFn: () => api.adminHealth(),
    refetchInterval: 60_000,
  });
  const crons = useQuery({
    queryKey: ["console", "crons", "health"],
    queryFn: () => api.crons.health(),
    refetchInterval: 60_000,
  });
  const pulses = useQuery({
    queryKey: ["console", "admin", "pulse"],
    queryFn: () => api.adminPulse(),
    refetchInterval: 60_000,
  });
  const cacheStats = useQuery({
    queryKey: ["console", "admin", "cache-stats"],
    queryFn: () => api.adminCacheStats("24h"),
    refetchInterval: 5 * 60_000,
  });
  const storage = useQuery({
    queryKey: ["console", "admin", "storage-projection"],
    queryFn: () => api.adminStorageProjection(),
    refetchInterval: 10 * 60_000,
  });
  const authSpike = useQuery({
    queryKey: ["console", "admin", "auth-spike"],
    queryFn: () => api.adminAuthSpike(),
    refetchInterval: 5 * 60_000,
  });

  return (
    <>
      <PageHeader
        icon={LuActivity}
        title="Status"
        description="Live health of the services that back destroy.network. Refreshes every 15 seconds."
      />
      <div className="p-8 space-y-6">
        {health.isLoading && (
          <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
            <LuLoader className="w-4 h-4 animate-spin" /> Checking services...
          </div>
        )}

        {health.data && (
          <>
            <Card>
              <div className="px-5 py-4 flex items-center justify-between border-b border-[var(--color-border)]">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-subtle)]">
                    System status
                  </p>
                  <p className="text-lg font-semibold text-[var(--color-text)] mt-1 capitalize">
                    {health.data.status === "healthy"
                      ? "All systems operational"
                      : "Degraded service"}
                  </p>
                </div>
                <OverallChip status={health.data.status} />
              </div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                {SERVICES.map((s) => (
                  <ServiceRow
                    key={s.key}
                    label={s.label}
                    description={s.description}
                    icon={s.icon}
                    ok={health.data.checks[s.key]}
                  />
                ))}
              </div>
              <div className="px-5 py-3 border-t border-[var(--color-border)] text-[11px] text-[var(--color-text-subtle)] flex items-center justify-between">
                <span>
                  Last checked{" "}
                  {new Date(health.data.timestamp).toLocaleTimeString()}
                </span>
                <button
                  type="button"
                  onClick={() => health.refetch()}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                >
                  Recheck now
                </button>
              </div>
            </Card>

            {/* Quick health widgets row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {cacheStats.data && (
                <Card>
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
                      Cache hit rate (24h)
                    </p>
                    <p className="text-2xl font-semibold text-[var(--color-text)] mt-1 tabular-nums">
                      {cacheStats.data.hitRatePercent.toFixed(1)}%
                    </p>
                    <p className="text-[11px] text-[var(--color-text-subtle)] mt-1">
                      {cacheStats.data.hits.toLocaleString()} hits ·{" "}
                      {cacheStats.data.misses.toLocaleString()} misses
                    </p>
                  </div>
                </Card>
              )}
              {storage.data && (
                <Card>
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
                      D1 row growth
                    </p>
                    <p className="text-2xl font-semibold text-[var(--color-text)] mt-1 tabular-nums">
                      {storage.data.d1Rows.rowsPerDay >= 0 ? "+" : ""}
                      {storage.data.d1Rows.rowsPerDay.toLocaleString()}
                      <span className="text-sm text-[var(--color-text-muted)] font-normal ml-1">
                        /day
                      </span>
                    </p>
                    <p className="text-[11px] text-[var(--color-text-subtle)] mt-1">
                      {storage.data.d1Rows.current.toLocaleString()} total rows
                      {storage.data.d1Rows.daysToDouble && (
                        <> · doubles in ~{storage.data.d1Rows.daysToDouble}d</>
                      )}
                    </p>
                  </div>
                </Card>
              )}
              {authSpike.data && (
                <Card
                  className={
                    authSpike.data.spike
                      ? "border-[rgba(237,66,69,0.5)]"
                      : ""
                  }
                >
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
                      Auth pressure (last hour)
                    </p>
                    <p
                      className={`text-2xl font-semibold mt-1 tabular-nums ${
                        authSpike.data.spike
                          ? "text-[#fca5a5]"
                          : "text-[var(--color-text)]"
                      }`}
                    >
                      {authSpike.data.lastHour.codes.toLocaleString()}
                      <span className="text-sm text-[var(--color-text-muted)] font-normal ml-1">
                        codes
                      </span>
                    </p>
                    <p className="text-[11px] text-[var(--color-text-subtle)] mt-1">
                      {authSpike.data.lastHour.verifyFailures.toLocaleString()} verify failures
                      {authSpike.data.spike && (
                        <span className="text-[#fca5a5] font-medium"> · SPIKE</span>
                      )}
                    </p>
                  </div>
                </Card>
              )}
            </div>

            {/* Liveness pulses (Stripe webhook, Resend, etc) */}
            {pulses.data && pulses.data.pulses.length > 0 && (
              <Card>
                <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center gap-2">
                  <LuClock className="w-4 h-4 text-[var(--color-text-subtle)]" />
                  <h2 className="text-sm font-semibold text-[var(--color-text)]">
                    Liveness pulses
                  </h2>
                </div>
                <div className="divide-y divide-[var(--color-border)]">
                  {pulses.data.pulses.map((p) => {
                    const ageSec = Math.floor(
                      (Date.now() - new Date(p.updatedAt).getTime()) / 1000,
                    );
                    const stale = ageSec > 60 * 60 * 24; // > 24h = stale
                    return (
                      <div
                        key={p.key}
                        className="px-5 py-3 flex items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              stale ? "bg-[#fcd34d]" : "bg-[#6ee7b7]"
                            }`}
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-mono text-[var(--color-text)]">
                              {p.key}
                            </p>
                            {p.value && (
                              <p className="text-[11px] text-[var(--color-text-subtle)] truncate">
                                {p.value}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className="text-[11px] text-[var(--color-text-muted)] font-mono shrink-0">
                          {formatAge(ageSec)} ago
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Cron job heartbeats */}
            {crons.data && crons.data.crons.length > 0 && (
              <Card>
                <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <LuTimer className="w-4 h-4 text-[var(--color-text-subtle)]" />
                    <h2 className="text-sm font-semibold text-[var(--color-text)]">
                      Cron jobs
                    </h2>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
                    <span className="text-[#6ee7b7]">{crons.data.summary.healthy} healthy</span>
                    {crons.data.summary.stale > 0 && (
                      <span className="text-[#fcd34d]">{crons.data.summary.stale} stale</span>
                    )}
                    {crons.data.summary.failing > 0 && (
                      <span className="text-[#fca5a5]">{crons.data.summary.failing} failing</span>
                    )}
                  </div>
                </div>
                <div className="divide-y divide-[var(--color-border)]">
                  {crons.data.crons.map((c) => {
                    const tone =
                      c.lastStatus === "failure"
                        ? "text-[#fca5a5]"
                        : c.stale
                          ? "text-[#fcd34d]"
                          : "text-[#6ee7b7]";
                    const Icon =
                      c.lastStatus === "failure"
                        ? LuCircleX
                        : c.stale
                          ? LuTriangleAlert
                          : LuCircleCheck;
                    return (
                      <div
                        key={c.cronName}
                        className="px-5 py-3 flex items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Icon className={`w-4 h-4 shrink-0 ${tone}`} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[var(--color-text)] truncate">
                              {c.cronName}
                            </p>
                            <p className="text-[11px] text-[var(--color-text-subtle)] font-mono truncate">
                              {c.account ? `${c.account} · ` : ""}
                              last {c.lastStatus} {formatAge(c.ageSeconds)} ago
                              {c.consecutiveFailures > 0 && (
                                <span className="text-[#fca5a5]">
                                  {" "}
                                  · {c.consecutiveFailures} fails
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <span className="flex items-center gap-1 text-[11px] text-[var(--color-text-subtle)] font-mono shrink-0">
                          <LuClock className="w-3 h-3" />
                          {new Date(c.lastRunAt).toLocaleTimeString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            <Card>
              <div className="px-5 py-4 border-b border-[var(--color-border)]">
                <h2 className="text-sm font-semibold text-[var(--color-text)]">
                  About these checks
                </h2>
              </div>
              <div className="p-5 text-sm text-[var(--color-text-muted)] space-y-2">
                <p>
                  Each service runs a single, cheap probe. A failure here means
                  user-facing requests in the same path will likely degrade or
                  error - it's the same code path the public API uses.
                </p>
                <p>
                  For richer SLO and uptime history (uptime %, p99 response
                  time, individual probe history), see the{" "}
                  <span className="text-[var(--color-accent-light)]">
                    Monitoring
                  </span>{" "}
                  page.
                </p>
              </div>
            </Card>
          </>
        )}
      </div>
    </>
  );
}

function OverallChip({ status }: { status: "healthy" | "degraded" }) {
  const ok = status === "healthy";
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-md text-xs font-medium border ${
        ok
          ? "bg-[rgba(66,184,131,0.1)] text-[#6ee7b7] border-[rgba(66,184,131,0.3)]"
          : "bg-[rgba(237,66,69,0.1)] text-[#fca5a5] border-[rgba(237,66,69,0.3)]"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          ok ? "bg-[#6ee7b7]" : "bg-[#fca5a5]"
        } animate-pulse`}
      />
      {ok ? "Operational" : "Degraded"}
    </span>
  );
}

function ServiceRow({
  label,
  description,
  icon: Icon,
  ok,
}: {
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  ok: boolean;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] flex items-center justify-center">
            <Icon className="w-3.5 h-3.5 text-[var(--color-text-muted)]" />
          </div>
          <p className="text-sm font-medium text-[var(--color-text)]">{label}</p>
        </div>
        {ok ? (
          <LuCircleCheck className="w-5 h-5 text-[#6ee7b7]" />
        ) : (
          <LuCircleX className="w-5 h-5 text-[#fca5a5]" />
        )}
      </div>
      <p className="text-xs text-[var(--color-text-subtle)]">{description}</p>
    </div>
  );
}

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}
